import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { 
  Shield, CheckCircle, XCircle, Search, Download, 
  Clock, MapPin, User, Hash, FileText, 
  Copy, CheckCheck
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../apiConfig";


const CertificatePage = () => {
  const { certificateId, verificationCode } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState("");
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (certificateId) {
      fetchCertificate();
    } else if (verificationCode) {
      verifyCertificate(verificationCode);
    } else {
      setLoading(false);
    }
  }, [certificateId, verificationCode]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/${certificateId}`);
      setCertificate(response.data);
    } catch (error) {
      console.error("Error fetching certificate:", error);
    } finally {
      setLoading(false);
    }
  };

  const verifyCertificate = async (code) => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/verify-certificate/${code}`);
      setVerifyResult(response.data);
    } catch (error) {
      setVerifyResult({ valid: false, message: "Certificate not found" });
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchCode.trim()) {
      verifyCertificate(searchCode.trim());
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const downloadPDF = async () => {
    try {
      const certId = certificate?.certificate_id || certificateId;
      const response = await axios.get(`${API}/certificates/${certId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${certId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download certificate");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="certificate-page">
      <Navigation />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-medium mb-4">Certificate of Authenticity</h1>
            <p className="text-muted-foreground">
              Verify the authenticity of your Aboriginal artwork
            </p>
          </motion.div>

          {/* Verification Search */}
          {!certificateId && !verificationCode && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-2xl border border-border p-8 mb-8"
            >
              <h2 className="font-medium mb-4">Verify a Certificate</h2>
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                    placeholder="Enter verification code (e.g., A1B2C3D4)"
                    className="pl-10 rounded-xl"
                    data-testid="verify-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="rounded-xl"
                  disabled={searching}
                  data-testid="verify-btn"
                >
                  {searching ? "Verifying..." : "Verify"}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Verification Result */}
          {verifyResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-2xl border p-8 text-center mb-8 ${
                verifyResult.valid
                  ? "bg-emerald-500/10 border-emerald-500/30"
                  : "bg-destructive/10 border-destructive/30"
              }`}
            >
              {verifyResult.valid ? (
                <>
                  <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-medium mb-2" data-testid="verify-success">
                    Certificate Verified
                  </h2>
                  {verifyResult.verified && verifyResult.issue_date && (
                    <Badge className="mb-4 bg-amber-500/20 text-amber-600 border-amber-500/30">
                      <Link2 className="h-3 w-3 mr-1" />
                      Verified Authentic
                    </Badge>
                  )}
                  <p className="text-muted-foreground mb-6">
                    This is an authentic certificate from Art By Arawol
                  </p>
                  <div className="text-left bg-background rounded-xl p-6 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Certificate ID</span>
                      <span className="font-mono">{verifyResult.certificate_id}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Artwork</span>
                      <span className="font-medium">{verifyResult.product_title}</span>
                    </div>
                    {verifyResult.artist_name && (
                      <>
                        <Separator />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Artist</span>
                          <span>{verifyResult.artist_name}</span>
                        </div>
                      </>
                    )}
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Registered Owner</span>
                      <span>{verifyResult.owner_name}</span>
                    </div>
                    {verifyResult.edition_info && (
                      <>
                        <Separator />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Edition</span>
                          <span>{verifyResult.edition_info}</span>
                        </div>
                      </>
                    )}
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Issued</span>
                      <span>{formatDate(verifyResult.issue_date)}</span>
                    </div>
                    {verifyResult.provenance_count > 0 && (
                      <>
                        <Separator />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Provenance Records</span>
                          <span>{verifyResult.provenance_count} ownership records</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* View Full Certificate Link */}
                  <div className="mt-6">
                    <Link to={`/certificate/${verifyResult.certificate_id}`}>
                      <Button variant="outline" className="rounded-full">
                        <FileText className="h-4 w-4 mr-2" />
                        View Full Certificate
                      </Button>
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                  <h2 className="text-2xl font-medium mb-2" data-testid="verify-failed">
                    Certificate Not Found
                  </h2>
                  <p className="text-muted-foreground">
                    The verification code you entered does not match any certificate in our records.
                    Please check the code and try again.
                  </p>
                </>
              )}
            </motion.div>
          )}

          {/* Full Certificate Display */}
          {certificate && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card rounded-2xl border border-border overflow-hidden"
              data-testid="certificate-display"
            >
              {/* Certificate Header */}
              <div className="bg-gradient-to-r from-amber-500/10 to-primary/10 p-8 text-center border-b border-border">
                <div className="flex justify-center gap-2 mb-4">
                  <Badge className="bg-emerald-500 text-white">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Authentic
                  </Badge>
                </div>
                <h2 className="text-2xl font-medium mb-2">Certificate of Authenticity</h2>
                <p className="text-sm text-muted-foreground font-mono">
                  {certificate.certificate_id}
                </p>
              </div>

              {/* Artwork Info */}
              <div className="p-8">
                <div className="flex gap-6 mb-8">
                  <div className="w-32 h-32 rounded-xl overflow-hidden bg-muted shrink-0">
                    <img
                      src={certificate.product?.images?.[0] || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                      alt={certificate.artwork_title || certificate.product?.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-1">
                      {certificate.artwork_title || certificate.product?.title}
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      by {certificate.artist_name || certificate.artist?.name}
                    </p>
                    {certificate.edition_info && (
                      <Badge variant="outline">
                        {certificate.edition_info}
                      </Badge>
                    )}
                    {!certificate.edition_info && certificate.product?.is_limited_edition && (
                      <Badge variant="outline">
                        Edition {certificate.product.edition_number} / {certificate.product.edition_total}
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Artwork Details */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Artwork Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Medium</p>
                      <p>{certificate.artwork_medium || certificate.product?.medium || "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Dimensions</p>
                      <p>{certificate.artwork_dimensions || certificate.product?.dimensions || "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Year Created</p>
                      <p>{certificate.artwork_year || certificate.product?.year_created || "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Issue Date</p>
                      <p>{formatDate(certificate.issue_date)}</p>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Owner & Certificate Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Certificate Details
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-sm">Current Owner</p>
                        <p className="font-medium">{certificate.current_owner_name || certificate.owner_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Hash className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-sm">Verification Code</p>
                        <div className="flex items-center gap-2">
                          <p className="font-mono bg-muted px-3 py-1 rounded-lg">
                            {certificate.verification_code}
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(certificate.verification_code)}
                          >
                            {copied ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Provenance History */}
                {certificate.provenance && certificate.provenance.length > 0 && (
                  <>
                    <Separator className="my-6" />
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                        Provenance History
                      </h4>
                      <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                        <div className="space-y-4">
                          {certificate.provenance.map((record, index) => (
                            <div key={record.record_id || index} className="relative pl-10">
                              <div className="absolute left-2.5 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                              <div className="bg-muted/50 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <p className="font-medium">{record.owner_name}</p>
                                    <Badge variant="outline" className="text-xs">
                                      {record.owner_type}
                                    </Badge>
                                  </div>
                                  {record.verified && (
                                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Verified
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(record.acquired_date)}
                                  </div>
                                  {record.location && (
                                    <div className="flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {record.location}
                                    </div>
                                  )}
                                </div>
                                {record.notes && (
                                  <p className="text-sm text-muted-foreground mt-2 italic">
                                    "{record.notes}"
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Certificate Hash */}
                {certificate.certificate_hash && (
                  <>
                    <Separator className="my-6" />
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                        Certificate Hash
                      </h4>
                      <div className="bg-muted rounded-lg p-3 overflow-x-auto">
                        <code className="text-xs font-mono text-muted-foreground break-all">
                          {certificate.certificate_hash}
                        </code>
                      </div>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-8">
                  <Button 
                    onClick={downloadPDF}
                    className="flex-1 rounded-xl"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-muted/50 p-6 text-center text-sm text-muted-foreground">
                <p>
                  This certificate guarantees the authenticity of the artwork and its provenance.
                  Verify at <span className="text-primary">arawol.gallery/verify</span>
                </p>
              </div>
            </motion.div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="animate-pulse">
              <div className="bg-card rounded-2xl p-8 border border-border">
                <div className="h-8 bg-muted rounded w-1/2 mx-auto mb-4" />
                <div className="h-4 bg-muted rounded w-1/3 mx-auto" />
              </div>
            </div>
          )}

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/products">
              <Button variant="outline" className="rounded-full">
                Browse Collection
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CertificatePage;
